import {
  buildPenPathD,
  getVisibleHandles,
  isPenPath,
  LinearElementEditor,
} from "@excalidraw/element";

import type {
  ExcalidrawLinearElement,
  ElementsMap,
} from "@excalidraw/element/types";
import type { NonDeleted } from "@excalidraw/element/types";

import { editorAccentColor, editorAccentRgb } from "./helpers";

import type { InteractiveCanvasAppState } from "../types";

/**
 * Draws the bezier handles (tangent line anchor→handle + a filled dot at each
 * handle) for the SELECTED anchors of a pen path in edit mode. Colors and
 * zoom-scaling mirror renderLinearPointHandles' selected-point styling.
 */
export const renderPenHandles = (
  context: CanvasRenderingContext2D,
  appState: InteractiveCanvasAppState,
  element: NonDeleted<ExcalidrawLinearElement>,
  elementsMap: ElementsMap,
) => {
  if (!isPenPath(element)) {
    return;
  }
  const editor = appState.selectedLinearElement;
  // render handles in edit mode (selected anchors) OR live while the path is
  // being drawn (all anchors, so the user sees every curve's tangents form)
  const isDrawing = appState.newElement?.id === element.id;
  if (!editor?.isEditing && !isDrawing) {
    return;
  }
  const indices = isDrawing
    ? element.points.map((_, i) => i)
    : editor?.selectedPointsIndices ?? null;
  const visible = getVisibleHandles(element, indices);

  context.save();
  context.translate(appState.scrollX, appState.scrollY);
  context.lineWidth = 1 / appState.zoom.value;
  context.setLineDash([]);

  // path spine: thin accent outline over every segment (Figma vector-edit
  // style) so the true curve stays visible whatever the stroke color/width
  {
    context.save();
    const cx = element.x + element.width / 2;
    const cy = element.y + element.height / 2;
    context.translate(cx, cy);
    context.rotate(element.angle);
    context.translate(element.x - cx, element.y - cy);
    context.strokeStyle = editorAccentColor(appState.theme);
    context.stroke(
      new Path2D(
        buildPenPathD(element.points, element.handles, !!element.polygon),
      ),
    );
    context.restore();
  }

  if (visible.length === 0) {
    context.restore();
    return;
  }

  // handle dot: a rounded square (Figma-style), slightly smaller than anchors
  const side =
    (LinearElementEditor.POINT_HANDLE_SIZE * 0.8) / appState.zoom.value;
  const cornerRadius = side * 0.3;

  for (const { anchor, absolute } of visible) {
    const anchorGlobal = LinearElementEditor.getPointGlobalCoordinates(
      element,
      element.points[anchor],
      elementsMap,
    );
    const handleGlobal = LinearElementEditor.getPointGlobalCoordinates(
      element,
      absolute,
      elementsMap,
    );

    context.strokeStyle = editorAccentColor(appState.theme);
    context.beginPath();
    context.moveTo(anchorGlobal[0], anchorGlobal[1]);
    context.lineTo(handleGlobal[0], handleGlobal[1]);
    context.stroke();

    // handle dot — rounded square
    context.fillStyle = `rgba(${editorAccentRgb(appState.theme)}, 0.9)`;
    context.beginPath();
    if (context.roundRect) {
      context.roundRect(
        handleGlobal[0] - side / 2,
        handleGlobal[1] - side / 2,
        side,
        side,
        cornerRadius,
      );
    } else {
      context.rect(
        handleGlobal[0] - side / 2,
        handleGlobal[1] - side / 2,
        side,
        side,
      );
    }
    context.fill();
    context.stroke();
  }

  context.restore();
};
